import * as z from "zod";
import { toKg, validateWeight, WeightIssue } from "@/utils/weight";
import { InspectionDeviationProduct, InspectionDeviationType } from "./types";
import {
  findDeviationProduct,
  findDeviationType,
  InspectionFormValues,
  InspectionStep,
  isPerOrder,
  NumberValue,
  toNumber,
} from "./useInspectionForm";
import { MAX_WEIGHT_KG } from "./utils";

// The backend's limits (POST /app/api/inspections/orders/{orderId}).
export const MAX_PHOTOS_PER_STREAM = 20;
export const MAX_ADDITIONAL_STREAMS = 10;
/** Rows in both deviation tables together. */
export const MAX_DEVIATIONS = 20;
export const MAX_REGISTRATION_NUMBER_LENGTH = 20;
export const MAX_PO_NUMBER_LENGTH = 50;

const isPositive = (value: NumberValue) => toNumber(value) > 0;

interface InspectionSchemaDeps {
  /** The deviation services on the order's contract. */
  deviationTypes: InspectionDeviationType[];
  /** The inspecting company's active deviation products. */
  deviationProducts: InspectionDeviationProduct[];
  /** A market order has no contract, and can't have deviations. */
  hasContract: boolean;
}

const numberValue = z.union([z.number(), z.string()]);

/** What a stream's weight message says for each issue. Zero or less reads as missing, as it did before the unit. */
const STREAM_WEIGHT_MESSAGES: Record<WeightIssue, string> = {
  required: "validation:inspection.weightRequired",
  notPositive: "validation:inspection.weightRequired",
  notWholeKg: "validation:weight.notWholeKg",
  tooLarge: "validation:inspection.weightTooLarge",
};

const streamSchema = z.object({
  key: z.string(),
  contractId: z.number().nullable(),
  weight: z.object({ amount: numberValue, unit: z.enum(["KG", "TONNES"]) }),
  photos: z.array(z.custom<File>()),
});

const deviationProductSchema = z.object({
  deviationProductId: z.number().nullable(),
  weightKg: numberValue,
  photos: z.array(z.custom<File>()),
});

const deviationServiceSchema = z.object({
  deviationTypeId: z.number().nullable(),
  registeredNumber: numberValue,
  photos: z.array(z.custom<File>()),
});

/**
 * The inspection form's rules. The messages are translation keys. The backend enforces the same rules;
 * these give the inspector the feedback before submitting.
 */
const createInspectionSchema = ({ deviationTypes, deviationProducts, hasContract }: InspectionSchemaDeps) =>
  z
    .object({
      transportRegistrationNumber: z.string(),
      poNumber: z.string(),
      comment: z.string(),
      multiFraction: z.boolean(),
      streams: z.array(streamSchema).min(1),
      hasDeviation: z.boolean(),
      deviationProducts: z.array(deviationProductSchema),
      deviationServices: z.array(deviationServiceSchema),
    })
    .superRefine((values, ctx) => {
      const issue = (path: (string | number)[], message: string) =>
        ctx.addIssue({ code: z.ZodIssueCode.custom, path, message });

      values.streams.forEach((stream, index) => {
        // The original order keeps its own contract (none for market orders); extra streams pick one.
        if (index > 0 && stream.contractId === null) {
          issue(["streams", index, "contractId"], "validation:inspection.contractRequired");
        }
        const weightIssue = validateWeight(stream.weight);
        if (weightIssue) issue(["streams", index, "weight"], STREAM_WEIGHT_MESSAGES[weightIssue]);
        if (!stream.photos.length) issue(["streams", index, "photos"], "validation:inspection.photoRequired");
        if (stream.photos.length > MAX_PHOTOS_PER_STREAM) {
          issue(["streams", index, "photos"], "validation:inspection.tooManyPhotos");
        }
      });

      if (!values.hasDeviation) return;

      if (!hasContract) {
        issue(["hasDeviation"], "validation:inspection.deviationsNeedContract");
        return;
      }

      const checkPhotos = (photos: File[], path: (string | number)[]) => {
        if (!photos.length) issue(path, "validation:inspection.photoRequired");
        if (photos.length > MAX_PHOTOS_PER_STREAM) issue(path, "validation:inspection.tooManyPhotos");
      };

      // The products are taken out of the original order's weight, so together they can weigh at most all of it.
      const productsKg = values.deviationProducts.reduce((sum, row) => sum + toNumber(row.weightKg), 0);
      const originalWeightKg = toKg(values.streams[0].weight) ?? 0;

      values.deviationProducts.forEach((row, index) => {
        const path = (field: string) => ["deviationProducts", index, field];
        if (index >= MAX_DEVIATIONS) issue(path("deviationProductId"), "validation:inspection.tooManyDeviations");
        if (!findDeviationProduct(deviationProducts, row)) {
          issue(path("deviationProductId"), "validation:inspection.deviationProductRequired");
        }
        if (!isPositive(row.weightKg)) issue(path("weightKg"), "validation:inspection.deviationWeightRequired");
        else if (toNumber(row.weightKg) > MAX_WEIGHT_KG)
          issue(path("weightKg"), "validation:inspection.weightTooLarge");
        else if (productsKg > originalWeightKg) {
          issue(path("weightKg"), "validation:inspection.productWeightExceedsOrder");
        }
        checkPhotos(row.photos, path("photos"));
      });

      values.deviationServices.forEach((service, index) => {
        const path = (field: string) => ["deviationServices", index, field];
        const type = findDeviationType(deviationTypes, service);
        // The limit counts both tables, products first.
        if (values.deviationProducts.length + index >= MAX_DEVIATIONS) {
          issue(path("deviationTypeId"), "validation:inspection.tooManyDeviations");
        }
        if (!type) issue(path("deviationTypeId"), "validation:inspection.deviationTypeRequired");
        // A "per order" service is counted once, so it has no number to fill in.
        if (!isPerOrder(type) && !isPositive(service.registeredNumber)) {
          issue(path("registeredNumber"), "validation:inspection.registeredNumberRequired");
        }
        checkPhotos(service.photos, path("photos"));
      });
    });

/** Validation errors as translation keys, keyed by field path (e.g. "streams.0.photos"). */
export type InspectionErrors = Record<string, string>;

export const validateInspection = (values: InspectionFormValues, deps: InspectionSchemaDeps): InspectionErrors => {
  const result = createInspectionSchema(deps).safeParse(values);
  if (result.success) return {};
  return result.error.issues.reduce<InspectionErrors>((errors, issue) => {
    const key = issue.path.join(".");
    errors[key] ??= issue.message;
    return errors;
  }, {});
};

/** The weight step covers the streams and the deviation step the deviations; the review step submits everything. */
export const hasErrorsForStep = (errors: InspectionErrors, step: InspectionStep) =>
  Object.keys(errors).some((key) =>
    step === "weighing"
      ? key.startsWith("streams")
      : step === "deviations"
        ? key === "hasDeviation" || key.startsWith("deviationProducts") || key.startsWith("deviationServices")
        : true
  );
